import React from 'react';
import logoRecibo from '../../assets/logo-recibo.png';

const TIPOS_LENTE = [
    'MONOFOCAL',
    'PROGRESIVO',
    'B. FLAT TOP',
    'B. INVISIBLE',
];

const MATERIALES = [
    'CR-39 1.49',
    'ÍNDICE 1.56',
    'ÍNDICE 1.61 / 1.60',
    'ÍNDICE 1.67',
    'ÍNDICE 1.74',
    'CRISTAL 1.52',
    'CRISTAL 1.70',
    'TRIVEX 1.53',
    'POLICARBONATO 1.59',
];

const CARACTERISTICAS = [
    'AR VERDE',
    'AR AZUL',
    'FILTRO AZUL',
    'FOTOCROMÁTICO',
    'ANTI UV',
    'TINTURADO',
    'POLARIZADO',
    'ANTIRAYAS',
    'TRANSITION',
];

/**
 * Copia del recibo lista para capturarla como PDF: html2canvas dibuja mal los campos de formulario,
 * así que cada input se cambia por un bloque con su texto.
 */
export const prepararReciboPDF = (documento) => {
    const copia = documento.cloneNode(true);
    copia.querySelectorAll('input, textarea').forEach(campo => {
        const bloque = document.createElement('div');
        bloque.className = campo.className;
        bloque.style.color = '#0f172a';
        bloque.style.fontWeight = '700';
        let texto = campo.value;
        if (campo.type === 'date') texto = texto.split('-').reverse().join('/');
        if (campo.type === 'number') texto = `$ ${(Number(texto) || 0).toFixed(2)}`;
        if (campo.tagName === 'TEXTAREA') bloque.style.minHeight = `${campo.rows * 21}px`;
        if (campo.closest('.rc-tabla-refraccion')) {
            bloque.style.minHeight = '20px';
            bloque.style.lineHeight = '20px';
            bloque.style.textAlign = 'center';
            bloque.style.fontSize = '0.84rem';
        }
        if (campo.classList.contains('rc-line-fill')) bloque.style.lineHeight = '20px';
        bloque.textContent = texto;
        campo.replaceWith(bloque);
    });
    copia.style.width = '680px';
    copia.style.maxWidth = '680px';
    return copia;
};

/**
 * Componente que replica fielmente el talonario físico "DETALLE DE COMPRA / RECIBO" de Vision Click.
 *
 * Props:
 *  - datos: Objeto con todos los campos del recibo
 *  - editable: Boolean (si los campos y checkboxes pueden ser interactivos o son solo lectura)
 *  - onChange: Función (campo, valor) para actualizar datos cuando es interactivo
 */
const ReciboVentaDocumento = ({ datos = {}, editable = false, onChange = () => {} }) => {
    const d = {
        numero: datos.numero || '',
        nombre: datos.cliente_nombre || datos.nombre || '',
        fecha: datos.fecha_emision || datos.fecha || new Date().toISOString().slice(0, 10),
        telefono: datos.cliente_telefono || datos.telefono || '',
        armazon: datos.armazon || '',

        // Refracción OD
        esfera_od: datos.esfera_od || '',
        cilindro_od: datos.cilindro_od || '',
        eje_od: datos.eje_od || '',
        adicion_od: datos.adicion_od || '',
        dp_od: datos.dp_od || '',

        // Refracción OI
        esfera_oi: datos.esfera_oi || '',
        cilindro_oi: datos.cilindro_oi || '',
        eje_oi: datos.eje_oi || '',
        adicion_oi: datos.adicion_oi || '',
        dp_oi: datos.dp_oi || '',

        // Checkboxes
        tipo_lente: datos.tipo_lente || [],
        materiales: datos.materiales || [],
        caracteristicas: datos.caracteristicas || [],

        observaciones: datos.observaciones || '',

        recibi_de: datos.recibi_de || datos.cliente_nombre || datos.nombre || '',
        la_cantidad_de: datos.la_cantidad_de || '',

        total: Number(datos.total) || 0,
        abono: Number(datos.abono) || 0,
        saldo: Number(datos.saldo !== undefined ? datos.saldo : (Number(datos.total || 0) - Number(datos.abono || 0))) || 0,
    };

    const toggleLista = (campo, item) => {
        if (!editable) return;
        const actual = Array.isArray(d[campo]) ? [...d[campo]] : [];
        const index = actual.indexOf(item);
        if (index > -1) {
            actual.splice(index, 1);
        } else {
            actual.push(item);
        }
        onChange(campo, actual);
    };

    const tiene = (campo, item) => (d[campo] || []).includes(item);

    const columnaOpciones = (titulo, campo, opciones) => (
        <div>
            <div className="rc-col-titulo">{titulo}</div>
            {opciones.map(op => {
                const marcado = tiene(campo, op);
                return (
                    <div key={op} className={`rc-item-check${editable ? ' rc-item-editable' : ''}`} onClick={() => toggleLista(campo, op)}>
                        <span>{op}</span>
                        <span className="rc-box">{marcado ? '✔' : ''}</span>
                    </div>
                );
            })}
        </div>
    );

    const filaOjo = (etiqueta, ojo) => (
        <tr>
            <td className="rc-ojo">{etiqueta}</td>
            {['esfera', 'cilindro', 'eje', 'adicion', 'dp'].map(campo => (
                <td key={campo}>
                    <input
                        value={d[`${campo}_${ojo}`]}
                        readOnly={!editable}
                        onChange={e => onChange(`${campo}_${ojo}`, e.target.value)}
                    />
                </td>
            ))}
        </tr>
    );

    return (
        <div className="recibo-talonario-container">
            <style>{`
                .recibo-talonario-container {
                    --rc-tinta: #22409a;
                    background: #ffffff;
                    color: var(--rc-tinta);
                    font-family: Arial, Helvetica, sans-serif;
                    width: 100%;
                    max-width: 680px;
                    margin: 0 auto;
                    border: 2px solid var(--rc-tinta);
                    border-radius: 7px;
                    box-sizing: border-box;
                    position: relative;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }

                .recibo-talonario-container input,
                .recibo-talonario-container textarea {
                    font-family: Arial, Helvetica, sans-serif;
                    color: #0f172a;
                    outline: none;
                }

                /* Encabezado: logo a la izquierda, datos a la derecha */
                .rc-encabezado {
                    display: grid;
                    grid-template-columns: 205px 1fr;
                }

                .rc-logo-block {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 12px 14px;
                    border-right: 2px solid var(--rc-tinta);
                }

                .rc-logo-img {
                    width: 100%;
                    height: auto;
                    display: block;
                }

                .rc-meta-block {
                    padding: 8px 12px 6px;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .rc-top-titles {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 8px;
                    margin-bottom: 4px;
                }

                .rc-titulos {
                    font-family: Rockwell, 'Roboto Slab', Georgia, 'Times New Roman', serif;
                    font-weight: 700;
                    font-size: 1.1rem;
                    line-height: 1.35;
                }

                .rc-num-recibo {
                    font-size: 1.55rem;
                    color: #c8202f;
                    letter-spacing: 1px;
                    line-height: 1.1;
                }

                .rc-linea {
                    display: flex;
                    align-items: flex-end;
                    gap: 5px;
                    font-size: 0.82rem;
                }

                .rc-linea > span {
                    white-space: nowrap;
                    line-height: 1.5;
                }

                .rc-line-fill {
                    flex: 1;
                    min-width: 0;
                    border: none;
                    border-bottom: 1.2px solid var(--rc-tinta);
                    border-radius: 0;
                    background: transparent;
                    font-weight: 700;
                    font-size: 0.86rem;
                    padding: 0 4px;
                    height: 20px;
                }

                .rc-seccion {
                    padding: 0 12px;
                }

                /* Tabla de refracción */
                .rc-tabla-refraccion {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                    border: 2px solid var(--rc-tinta);
                    border-radius: 6px;
                    margin: 6px 0 12px;
                    table-layout: fixed;
                }

                .rc-tabla-refraccion th,
                .rc-tabla-refraccion td {
                    border-right: 1.2px solid var(--rc-tinta);
                    border-bottom: 1.2px solid var(--rc-tinta);
                    padding: 3px 4px;
                    text-align: center;
                }

                .rc-tabla-refraccion th {
                    font-weight: 400;
                    font-size: 0.72rem;
                    color: var(--rc-tinta);
                }

                .rc-tabla-refraccion th:last-child,
                .rc-tabla-refraccion td:last-child {
                    border-right: none;
                }

                .rc-tabla-refraccion tr:last-child td {
                    border-bottom: none;
                }

                .rc-tabla-refraccion .rc-ojo {
                    font-size: 0.68rem;
                    width: 34px;
                }

                .rc-tabla-refraccion td input {
                    width: 100%;
                    height: 20px;
                    padding: 0;
                    border: none;
                    background: transparent;
                    text-align: center;
                    font-weight: 700;
                    font-size: 0.84rem;
                }

                /* Casillas en tres columnas */
                .rc-grid-opciones {
                    display: grid;
                    grid-template-columns: 0.82fr 1.1fr 1fr;
                    gap: 26px;
                    padding: 0 8px;
                    margin-bottom: 10px;
                }

                .rc-col-titulo {
                    font-size: 0.8rem;
                    font-weight: 700;
                    text-align: center;
                    margin-bottom: 9px;
                }

                .rc-item-check {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 6px;
                    font-size: 0.68rem;
                    margin-bottom: 4px;
                    user-select: none;
                }

                .rc-item-editable {
                    cursor: pointer;
                }

                .rc-box {
                    flex: none;
                    width: 16px;
                    height: 12px;
                    border: 1.2px solid var(--rc-tinta);
                    border-radius: 3px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.68rem;
                    font-weight: 700;
                    line-height: 1;
                }

                /* Observaciones: dos líneas de escritura, como en el papel */
                .rc-obs-row {
                    position: relative;
                    font-size: 0.82rem;
                    margin-bottom: 6px;
                }

                .rc-obs-row > span {
                    position: absolute;
                    left: 0;
                    top: 0;
                    line-height: 21px;
                }

                .rc-obs-texto {
                    display: block;
                    width: 100%;
                    box-sizing: border-box;
                    border: none;
                    padding: 0 4px;
                    resize: none;
                    overflow: hidden;
                    text-indent: 124px;
                    line-height: 21px;
                    font-size: 0.82rem;
                    font-weight: 700;
                    background-color: transparent;
                    background-image: linear-gradient(to bottom, transparent 20px, var(--rc-tinta) 20px, var(--rc-tinta) 21px);
                    background-size: 100% 21px;
                }

                .rc-pago {
                    border-top: 2px solid var(--rc-tinta);
                    padding: 8px 12px 10px;
                }

                .rc-pago .rc-linea {
                    margin-bottom: 7px;
                }

                /* Total, abono, saldo y firmas */
                .rc-pagos-firmas-grid {
                    display: grid;
                    grid-template-columns: 170px 1fr;
                    gap: 28px;
                    align-items: end;
                }

                .rc-totales-table {
                    border-collapse: collapse;
                    width: 100%;
                }

                .rc-totales-table td {
                    font-size: 0.8rem;
                    padding: 0;
                }

                .rc-totales-table td.lbl {
                    width: 58px;
                }

                .rc-totales-table td.val {
                    border: 1.2px solid var(--rc-tinta);
                    height: 24px;
                    padding: 0 6px;
                    text-align: right;
                    font-weight: 700;
                    color: #0f172a;
                }

                .rc-totales-table td.val input {
                    width: 100%;
                    border: none;
                    background: transparent;
                    padding: 0;
                    text-align: right;
                    font-weight: 700;
                    font-size: 0.8rem;
                }

                .rc-totales-table td.val input::-webkit-outer-spin-button,
                .rc-totales-table td.val input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                .rc-totales-table td.val input[type=number] {
                    -moz-appearance: textfield;
                }

                .rc-firmas-block {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 26px;
                }

                .rc-firma-linea {
                    border-top: 1.2px solid var(--rc-tinta);
                    padding-top: 3px;
                    text-align: center;
                    font-size: 0.74rem;
                }

                /* Política de no devolución */
                .rc-politica-box {
                    border-top: 2px solid var(--rc-tinta);
                    padding: 6px 12px 7px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    line-height: 1.3;
                    text-align: justify;
                }

                .rc-politica-tit {
                    font-family: Rockwell, 'Roboto Slab', Georgia, 'Times New Roman', serif;
                    font-size: 0.92rem;
                    text-align: center;
                    margin-bottom: 3px;
                }

                .rc-politica-nota {
                    font-size: 0.74rem;
                    text-align: center;
                    margin-top: 3px;
                }

                /* Pie con contactos */
                .rc-footer-info {
                    border-top: 2px solid var(--rc-tinta);
                    padding: 4px 12px 8px;
                    font-family: Rockwell, 'Roboto Slab', Georgia, 'Times New Roman', serif;
                    font-weight: 700;
                    font-size: 0.74rem;
                }

                .rc-footer-tit {
                    text-align: center;
                    font-size: 0.78rem;
                    margin-bottom: 3px;
                }

                .rc-footer-cols {
                    display: grid;
                    grid-template-columns: auto 1fr auto;
                    gap: 12px;
                    align-items: center;
                    line-height: 1.5;
                }

                .rc-footer-tel {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                }

                .rc-footer-tel i {
                    font-size: 1.5rem;
                    line-height: 1;
                }

                .rc-footer-dir {
                    text-align: center;
                }

                .rc-footer-red i {
                    margin-right: 5px;
                }

                @media (max-width: 560px) {
                    .rc-encabezado { grid-template-columns: 110px 1fr; }
                    .rc-logo-block { padding: 8px; }
                    .rc-grid-opciones { grid-template-columns: 1fr; gap: 10px; }
                    .rc-pagos-firmas-grid { grid-template-columns: 1fr; }
                    .rc-firmas-block { margin-top: 30px; }
                    .rc-footer-cols { grid-template-columns: 1fr; }
                    .rc-footer-dir { text-align: left; }
                }

                /* Estilo de Impresión para Talonario */
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .recibo-talonario-container, .recibo-talonario-container * {
                        visibility: visible;
                    }
                    .recibo-talonario-container {
                        position: fixed;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        background: #fff !important;
                    }
                    .recibo-talonario-container input::-webkit-calendar-picker-indicator {
                        display: none;
                    }
                    .recibo-talonario-container ::placeholder {
                        color: transparent;
                    }
                }
            `}</style>

            {/* ENCABEZADO */}
            <div className="rc-encabezado">
                <div className="rc-logo-block">
                    <img src={logoRecibo} alt="Vision Click" className="rc-logo-img" />
                </div>

                <div className="rc-meta-block">
                    <div className="rc-top-titles">
                        <div className="rc-titulos">
                            <div>DETALLE DE COMPRA</div>
                            <div>RECIBO</div>
                        </div>
                        <div className="rc-num-recibo">{d.numero}</div>
                    </div>

                    <div className="rc-linea">
                        <span>NOMBRE :</span>
                        <input
                            className="rc-line-fill"
                            value={d.nombre}
                            readOnly={!editable}
                            onChange={e => onChange('cliente_nombre', e.target.value.toUpperCase())}
                        />
                    </div>

                    <div className="rc-linea">
                        <span>FECHA:</span>
                        <input
                            className="rc-line-fill"
                            type={editable ? "date" : "text"}
                            value={d.fecha}
                            readOnly={!editable}
                            onChange={e => onChange('fecha_emision', e.target.value)}
                        />
                    </div>

                    <div className="rc-linea">
                        <span>TELÉFONO:</span>
                        <input
                            className="rc-line-fill"
                            value={d.telefono}
                            readOnly={!editable}
                            onChange={e => onChange('cliente_telefono', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="rc-seccion">
                {/* LÍNEA DE ARMAZÓN */}
                <div className="rc-linea" style={{ marginTop: 6 }}>
                    <span>ARMAZÓN:</span>
                    <input
                        className="rc-line-fill"
                        value={d.armazon}
                        readOnly={!editable}
                        onChange={e => onChange('armazon', e.target.value)}
                    />
                </div>

                {/* TABLA DE REFRACCIÓN */}
                <table className="rc-tabla-refraccion">
                    <thead>
                        <tr>
                            <th style={{ width: 34 }}><i className="bi bi-eye-fill"></i></th>
                            <th>ESFERA</th>
                            <th>CILINDRO</th>
                            <th>EJE</th>
                            <th>ADICIÓN</th>
                            <th style={{ width: '27%' }}>DISTANCIA PUPILAR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filaOjo('OD', 'od')}
                        {filaOjo('OI', 'oi')}
                    </tbody>
                </table>

                {/* 3 COLUMNAS CON CASILLAS */}
                <div className="rc-grid-opciones">
                    {columnaOpciones('TIPO LENTE', 'tipo_lente', TIPOS_LENTE)}
                    {columnaOpciones('MATERIALES', 'materiales', MATERIALES)}
                    {columnaOpciones('CARACTERÍSTICAS', 'caracteristicas', CARACTERISTICAS)}
                </div>

                {/* OBSERVACIONES */}
                <div className="rc-obs-row">
                    <span>OBSERVACIONES:</span>
                    <textarea
                        className="rc-obs-texto"
                        rows={Math.max(2, Math.ceil((d.observaciones.length + 18) / 78))}
                        value={d.observaciones}
                        readOnly={!editable}
                        onChange={e => onChange('observaciones', e.target.value.replace(/\n/g, ' '))}
                    />
                </div>
            </div>

            {/* RECIBÍ DE / LA CANTIDAD DE / PAGOS Y FIRMAS */}
            <div className="rc-pago">
                <div className="rc-linea">
                    <span>RECIBÍ DE:</span>
                    <input
                        className="rc-line-fill"
                        value={d.recibi_de}
                        readOnly={!editable}
                        onChange={e => onChange('recibi_de', e.target.value.toUpperCase())}
                    />
                </div>
                <div className="rc-linea">
                    <span>LA CANTIDAD DE:</span>
                    <input
                        className="rc-line-fill"
                        value={d.la_cantidad_de}
                        readOnly={!editable}
                        onChange={e => onChange('la_cantidad_de', e.target.value)}
                    />
                </div>

                <div className="rc-pagos-firmas-grid">
                    <table className="rc-totales-table">
                        <tbody>
                            <tr>
                                <td className="lbl">TOTAL</td>
                                <td className="val">$ {d.total.toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td className="lbl">ABONO</td>
                                <td className="val">
                                    {editable ? (
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max={d.total}
                                            value={d.abono}
                                            onChange={e => {
                                                const ab = Number(e.target.value) || 0;
                                                onChange('abono', ab);
                                                onChange('saldo', Math.max(0, d.total - ab));
                                            }}
                                        />
                                    ) : `$ ${d.abono.toFixed(2)}`}
                                </td>
                            </tr>
                            <tr>
                                <td className="lbl">SALDO</td>
                                <td className="val">$ {d.saldo.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="rc-firmas-block">
                        <div className="rc-firma-linea">RECIBÍ CONFORME</div>
                        <div className="rc-firma-linea">ENTREGUÉ CONFORME</div>
                    </div>
                </div>
            </div>

            {/* POLÍTICA DE NO DEVOLUCIÓN */}
            <div className="rc-politica-box">
                <div className="rc-politica-tit">POLÍTICA DE NO DEVOLUCIÓN</div>
                <div>
                    Todas las ventas son definitivas, no realizamos devoluciones o reembolsos. Apreciamos tu comprensión y te aseguramos que estamos comprometidos a entregar productos y servicios de alta calidad, estamos prestos a brindar solución a cualquier problema que pueda surgir.
                </div>
                <div className="rc-politica-nota">
                    Nota: Pasado los 30 días pierde derecho a su abono y a sus lentes.
                </div>
            </div>

            {/* INFORMACIÓN DE CONTACTO / FOOTER */}
            <div className="rc-footer-info">
                <div className="rc-footer-tit">INFORMACIÓN</div>
                <div className="rc-footer-cols">
                    <div className="rc-footer-tel">
                        <i className="bi bi-whatsapp"></i>
                        <div>
                            <div>VISION CLICK CENTRO 0998940200</div>
                            <div>VISION CLICK SUR 0993387008</div>
                        </div>
                    </div>
                    <div className="rc-footer-dir">
                        <div>Rafael Arellano y Ayacucho</div>
                        <div>Veintimilla y Camilo Ponce</div>
                    </div>
                    <div className="rc-footer-red">
                        <div><i className="bi bi-instagram"></i>@visio.nclick</div>
                        <div><i className="bi bi-facebook"></i>Visión Click</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReciboVentaDocumento;
